import React, { useContext, useEffect, useState } from 'react';
import { DataContext } from '../../Provider/dataProvider';
import { fetchWithAuth } from '../../utils/authUtils';
import '../../css/adminDashboard.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// Helper function to format date to Vietnam time
const formatVietnamDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (e) {
    return dateString;
  }
};

const AdminAlerts = () => {
  const { data } = useContext(DataContext);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({ riskLevel: '' });

  useEffect(() => {
    loadAlerts();
  }, [page, search, sortField, sortOrder, filters]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        sort: sortField,
        order: sortOrder,
      });

      if (search) params.append('search', search);
      if (filters.riskLevel) params.append('riskLevel', filters.riskLevel);

      const response = await fetchWithAuth(`${API_BASE}/api/admin/alerts?${params}`);
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.message || 'Không thể tải danh sách cảnh báo');
      }

      setAlerts(Array.isArray(payload?.data?.alerts) ? payload.data.alerts : []);
      setTotalPages(payload?.data?.totalPages || 1);
    } catch (err) {
      setError(err?.message || 'Lỗi khi tải danh sách cảnh báo');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleViewDetail = (alert) => {
    setSelectedAlert(alert);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa cảnh báo này?')) return;

    try {
      const response = await fetchWithAuth(`${API_BASE}/api/admin/alerts/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || 'Không thể xóa cảnh báo');
      }

      loadAlerts();
    } catch (err) {
      alert(err?.message || 'Lỗi khi xóa cảnh báo');
    }
  };

  const getRiskLevelLabel = (level) => {
    const labels = {
      high: 'Cao',
      medium: 'Trung bình',
      low: 'Thấp',
    };
    return labels[level] || level;
  };

  const getRiskLevelClass = (level) => {
    const classes = {
      high: 'admin-badge-danger',
      medium: 'admin-badge-warning',
      low: 'admin-badge-success',
    };
    return classes[level] || 'admin-badge-info';
  };

  if (loading) {
    return (
      <div className="admin-page-container">
        <div className="admin-loading">
          <i className="fa-solid fa-spinner fa-spin"></i>
          <p>Đang tải dữ liệu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page-container">
      <div className="admin-header">
        <div>
          <h1><i className="fa-solid fa-triangle-exclamation"></i> Quản lý cảnh báo</h1>
          <small>Quản lý và theo dõi các cảnh báo từ hệ thống AI</small>
        </div>
      </div>

      <div className="admin-table-card">
        <div className="admin-table-header">
          <div className="admin-search-box">
            <input
              type="text"
              placeholder="Tìm kiếm theo tên, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <i className="fa-solid fa-search"></i>
          </div>
          <div className="admin-filters">
            <select
              value={filters.riskLevel}
              onChange={(e) => setFilters({ ...filters, riskLevel: e.target.value })}
            >
              <option value="">Tất cả mức độ</option>
              <option value="high">Cao</option>
              <option value="medium">Trung bình</option>
              <option value="low">Thấp</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="admin-error">
            <i className="fa-solid fa-circle-exclamation"></i>
            <p>{error}</p>
          </div>
        )}

        <table className="admin-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('createdAt')}>
                Ngày {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('elderlyName')}>
                Người cao tuổi {sortField === 'elderlyName' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('riskLevel')}>
                Mức độ {sortField === 'riskLevel' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th>Loại cảnh báo</th>
              <th>Nội dung</th>
              <th>Hành động</th>
            </tr>
          </thead>
          <tbody>
            {alerts.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>
                  Không có dữ liệu
                </td>
              </tr>
            ) : (
              alerts.map((alert) => (
                <tr key={alert.id}>
                  <td>{formatVietnamDate(alert.createdAt)}</td>
                  <td>
                    <strong>{alert.elderlyName || alert.fullName || 'N/A'}</strong>
                    <br />
                    <small style={{ color: '#7f8c8d' }}>{alert.elderlyEmail || alert.email || ''}</small>
                  </td>
                  <td>
                    <span className={`admin-badge ${getRiskLevelClass(alert.riskLevel)}`}>
                      {getRiskLevelLabel(alert.riskLevel)}
                    </span>
                  </td>
                  <td>{alert.alertType || '-'}</td>
                  <td>
                    <div style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {alert.message || '-'}
                    </div>
                  </td>
                  <td className="admin-action-links">
                    <button
                      className="admin-btn admin-btn-view"
                      onClick={() => handleViewDetail(alert)}
                    >
                      <i className="fa-solid fa-eye"></i> Chi tiết
                    </button>
                    <button
                      className="admin-btn admin-btn-delete"
                      onClick={() => handleDelete(alert.id)}
                    >
                      <i className="fa-solid fa-trash"></i> Xóa
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="admin-pagination">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              <i className="fa-solid fa-chevron-left"></i> Trước
            </button>
            <span>Trang {page} / {totalPages}</span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
            >
              Sau <i className="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        )}
      </div>

      {showModal && selectedAlert && (
        <div className="admin-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Chi tiết cảnh báo</h3>
              <button className="admin-modal-close" onClick={() => setShowModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-info-grid">
                <div className="admin-info-item">
                  <label>ID</label>
                  <span>#{selectedAlert.id}</span>
                </div>
                <div className="admin-info-item">
                  <label>Người cao tuổi</label>
                  <span>{selectedAlert.elderlyName || selectedAlert.fullName || 'N/A'}</span>
                </div>
                <div className="admin-info-item">
                  <label>Email</label>
                  <span>{selectedAlert.elderlyEmail || selectedAlert.email || '-'}</span>
                </div>
                <div className="admin-info-item">
                  <label>Ngày tạo</label>
                  <span>{formatVietnamDate(selectedAlert.createdAt)}</span>
                </div>
                <div className="admin-info-item">
                  <label>Mức độ rủi ro</label>
                  <span className={`admin-badge ${getRiskLevelClass(selectedAlert.riskLevel)}`}>
                    {getRiskLevelLabel(selectedAlert.riskLevel)}
                  </span>
                </div>
                {selectedAlert.alertType && (
                  <div className="admin-info-item">
                    <label>Loại cảnh báo</label>
                    <span>{selectedAlert.alertType}</span>
                  </div>
                )}
                <div className="admin-info-item" style={{ gridColumn: 'span 2' }}>
                  <label>Nội dung</label>
                  <p style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: '10px', 
                    borderRadius: '6px',
                    marginTop: '5px'
                  }}>
                    {selectedAlert.message || selectedAlert.content || '-'}
                  </p>
                </div>
                {selectedAlert.relatedData && (
                  <div className="admin-info-item" style={{ gridColumn: 'span 2' }}>
                    <label>Dữ liệu liên quan</label>
                    <pre style={{ 
                      backgroundColor: '#f8f9fa', 
                      padding: '10px', 
                      borderRadius: '6px',
                      marginTop: '5px',
                      fontSize: '12px',
                      overflow: 'auto'
                    }}>
                      {JSON.stringify(selectedAlert.relatedData, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAlerts;
